import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuinielaStore } from '../../store/quinielaStore'
import { useAuthStore } from '../../store/authStore'
import { Calendar, DollarSign, Users, Trophy, FileText, Loader, AlertCircle, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import { getRounds, getFixturesByRound, LEAGUES, formatFixture } from '../../services/apiFootball'

export default function EditQuiniela() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { updateQuiniela, fetchQuinielaById, loading } = useQuinielaStore()
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    league: 'liga_mx',
    season: '2024',
    jornada: '',
    entryFee: '',
    startDate: '',
    endDate: '',
    maxLimit: '',
    matchIds: []
  })

  const [errors, setErrors] = useState({})
  const [rounds, setRounds] = useState([])
  const [availableFixtures, setAvailableFixtures] = useState([])
  const [selectedFixtures, setSelectedFixtures] = useState([])
  const [loadingFixtures, setLoadingFixtures] = useState(false)
  const [loadingQuiniela, setLoadingQuiniela] = useState(true)
  const [hasParticipants, setHasParticipants] = useState(false)

  // Cargar datos de la quiniela
  useEffect(() => {
    loadQuiniela()
  }, [id])

  // Cargar jornadas cuando cambie la temporada
  useEffect(() => {
    if (formData.season) {
      loadRounds()
    }
  }, [formData.season])

  const loadQuiniela = async () => {
    setLoadingQuiniela(true)
    const result = await fetchQuinielaById(id)
    
    if (result.success) {
      const quiniela = result.data
      
      // Verificar si tiene participantes
      setHasParticipants(quiniela.participants?.count > 0)
      
      // Convertir timestamps a formato datetime-local
      const startDate = quiniela.startDate?.toDate()
      const endDate = quiniela.endDate?.toDate()
      
      setFormData({
        name: quiniela.name || '',
        description: quiniela.description || '',
        league: quiniela.league || 'liga_mx',
        season: quiniela.season || '2024',
        jornada: quiniela.jornada?.toString() || '',
        entryFee: quiniela.entryFee?.toString() || '',
        startDate: startDate ? startDate.toISOString().slice(0, 16) : '',
        endDate: endDate ? endDate.toISOString().slice(0, 16) : '',
        maxLimit: quiniela.participants?.maxLimit?.toString() || '',
        matchIds: quiniela.matchIds || []
      })

      // Cargar jornadas para esta temporada
      await loadRounds(quiniela.season)
      
    } else {
      toast.error('Error al cargar la quiniela')
      navigate('/operator')
    }
    
    setLoadingQuiniela(false)
  }

  const loadRounds = async (season = null) => {
    try {
      let leagueId = LEAGUES.LIGA_MX
      if (formData.league === 'mundial') leagueId = LEAGUES.MUNDIAL
      if (formData.league === 'champions_league') leagueId = LEAGUES.CHAMPIONS_LEAGUE
      const seasonYear = parseInt(season || formData.season) || 2024
      const roundsData = await getRounds(leagueId, seasonYear)
      // Filtrar solo jornadas futuras
      const now = new Date()
      const futureRounds = []
      for (const round of roundsData) {
        const fixtures = await getFixturesByRound(leagueId, seasonYear, round)
        if (fixtures.some(f => new Date(f.fixture.date) > now)) {
          futureRounds.push(round)
        }
      }
      setRounds(futureRounds)
    } catch (error) {
      console.error('Error loading rounds:', error)
      toast.error('Error al cargar las jornadas: ' + error.message)
    }
  }

  const loadFixturesByRound = async (round) => {
    if (!round) return
    
    setLoadingFixtures(true)
    try {
      let leagueId = LEAGUES.LIGA_MX
      if (formData.league === 'mundial') leagueId = LEAGUES.MUNDIAL
      if (formData.league === 'champions_league') leagueId = LEAGUES.CHAMPIONS_LEAGUE
      const season = parseInt(formData.season) || 2024
      const fixtures = await getFixturesByRound(leagueId, season, round)
      
      const formattedFixtures = fixtures.map(formatFixture)
      setAvailableFixtures(formattedFixtures)
      
      // Seleccionar los partidos que ya estaban en la quiniela
      const preSelected = formattedFixtures.filter(f => 
        formData.matchIds.includes(f.id.toString())
      )
      setSelectedFixtures(preSelected)
      
      // Auto-calcular fechas de inicio y fin si no hay participantes
      if (!hasParticipants && formattedFixtures.length > 0) {
        const dates = formattedFixtures.map(f => f.date)
        const minDate = new Date(Math.min(...dates))
        const maxDate = new Date(Math.max(...dates))
        
        setFormData(prev => ({
          ...prev,
          startDate: minDate.toISOString().slice(0, 16),
          endDate: maxDate.toISOString().slice(0, 16)
        }))
      }
      
      toast.success(`${formattedFixtures.length} partidos cargados`)
    } catch (error) {
      console.error('Error loading fixtures:', error)
      toast.error('Error al cargar los partidos')
    } finally {
      setLoadingFixtures(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleJornadaChange = (e) => {
    const roundName = e.target.value
    
    // Extraer número de jornada del formato "Regular Season - 15"
    const jornadaMatch = roundName.match(/\d+$/)
    const jornadaNum = jornadaMatch ? jornadaMatch[0] : ''
    
    setFormData(prev => ({
      ...prev,
      jornada: jornadaNum
    }))
    
    if (errors.jornada) {
      setErrors(prev => ({ ...prev, jornada: '' }))
    }
    
    // Cargar partidos de esta jornada
    loadFixturesByRound(roundName)
  }

  const toggleFixture = (fixture) => {
    if (hasParticipants) {
      toast.error('No puedes modificar los partidos si ya hay participantes')
      return
    }

    const fixtureId = fixture.id.toString()
    const isSelected = selectedFixtures.some(f => f.id === fixture.id)
    
    if (isSelected) {
      setSelectedFixtures(prev => prev.filter(f => f.id !== fixture.id))
      setFormData(prev => ({
        ...prev,
        matchIds: prev.matchIds.filter(id => id !== fixtureId)
      }))
    } else {
      setSelectedFixtures(prev => [...prev, fixture])
      setFormData(prev => ({
        ...prev,
        matchIds: [...prev.matchIds, fixtureId]
      }))
    }
  }

  const selectAllFixtures = () => {
    if (hasParticipants) {
      toast.error('No puedes modificar los partidos si ya hay participantes')
      return
    }
    
    setSelectedFixtures([...availableFixtures])
    setFormData(prev => ({
      ...prev,
      matchIds: availableFixtures.map(f => f.id.toString())
    }))
    toast.success('Todos los partidos seleccionados')
  }

  const clearAllFixtures = () => {
    if (hasParticipants) {
      toast.error('No puedes modificar los partidos si ya hay participantes')
      return
    }
    
    setSelectedFixtures([])
    setFormData(prev => ({
      ...prev,
      matchIds: []
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido'
    }

    if (!formData.jornada || formData.jornada < 1) {
      newErrors.jornada = 'La jornada debe ser mayor a 0'
    }

    if (!formData.entryFee || parseFloat(formData.entryFee) < 50) {
      newErrors.entryFee = 'El costo mínimo es de $50 pesos'
    }

    if (!formData.startDate) {
      newErrors.startDate = 'La fecha de inicio es requerida'
    }

    if (!formData.endDate) {
      newErrors.endDate = 'La fecha de fin es requerida'
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate)
      const end = new Date(formData.endDate)
      
      if (end <= start) {
        newErrors.endDate = 'La fecha de fin debe ser posterior a la de inicio'
      }
    }

    if (formData.matchIds.length === 0) {
      newErrors.matchIds = 'Debes agregar al menos un partido'
    }

    if (formData.maxLimit && parseInt(formData.maxLimit) < 2) {
      newErrors.maxLimit = 'El límite debe ser al menos 2 participantes'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Por favor corrige los errores del formulario')
      return
    }

    const result = await updateQuiniela(id, formData)

    if (result.success) {
      toast.success('Quiniela actualizada exitosamente')
      navigate('/operator')
    }
  }

  if (loadingQuiniela) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center py-12">
          <Loader className="animate-spin text-accent-green" size={40} />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/operator')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} />
          Volver al panel
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Editar Quiniela</h1>
        <p className="text-gray-600">Actualiza los detalles de la quiniela</p>
        {hasParticipants && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Nota:</strong> Esta quiniela ya tiene participantes. Algunos campos no se pueden modificar.
            </p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información Básica */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FileText size={20} />
            Información Básica
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de la Quiniela *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input ${errors.name ? 'border-red-500' : ''}`}
                placeholder="Ej: Jornada 15 - Liga MX Apertura 2026"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.name}
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="input"
                placeholder="Descripción opcional de la quiniela"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Liga *
              </label>
              <select
                name="league"
                value={formData.league}
                onChange={handleChange}
                className="input bg-gray-100"
                disabled={hasParticipants}
              >
                <option value="liga_mx">Liga MX</option>
                <option value="mundial">Mundial</option>
                <option value="champions_league">Champions League</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Temporada *
              </label>
              <select
                name="season"
                value={formData.season}
                onChange={handleChange}
                className="input bg-gray-100"
                disabled={hasParticipants}
              >
                <option value="2024">2024</option>
                <option value="2023">2023</option>
                <option value="2022">2022</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Jornada *
              </label>
              <select
                name="round"
                value={`Regular Season - ${formData.jornada}`}
                onChange={handleJornadaChange}
                className={`input ${hasParticipants ? 'bg-gray-100' : ''} ${errors.jornada ? 'border-red-500' : ''}`}
                disabled={rounds.length === 0 || hasParticipants}
              >
                <option value="">Selecciona una jornada</option>
                {rounds.map((round, index) => (
                  <option key={index} value={round}>
                    {round}
                  </option>
                ))}
              </select>
              {errors.jornada && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.jornada}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Costo de Entrada * (MXN)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="number"
                  name="entryFee"
                  value={formData.entryFee}
                  onChange={handleChange}
                  min="50"
                  step="10"
                  className={`pl-10 input ${hasParticipants ? 'bg-gray-100' : ''} ${errors.entryFee ? 'border-red-500' : ''}`}
                  placeholder="100"
                  disabled={hasParticipants}
                />
              </div>
              {errors.entryFee && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.entryFee}
                </p>
              )}
              {hasParticipants && (
                <p className="mt-1 text-xs text-yellow-600">
                  No se puede modificar con participantes registrados
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Fechas */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Calendar size={20} />
            Fechas
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha y Hora del Primer Partido *
              </label>
              <input
                type="datetime-local"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className={`input ${errors.startDate ? 'border-red-500' : ''}`}
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.startDate}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                El registro se cierra 1 hora antes
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha y Hora del Último Partido *
              </label>
              <input
                type="datetime-local"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className={`input ${errors.endDate ? 'border-red-500' : ''}`}
              />
              {errors.endDate && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.endDate}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Partidos */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Trophy size={20} />
              Partidos de la Jornada
            </h2>
            {availableFixtures.length > 0 && !hasParticipants && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllFixtures}
                  className="text-sm text-accent-green hover:underline"
                  disabled={selectedFixtures.length === availableFixtures.length}
                >
                  Seleccionar todos
                </button>
                {selectedFixtures.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAllFixtures}
                    className="text-sm text-red-600 hover:underline"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            )}
          </div>
          
          {loadingFixtures && (
            <div className="flex justify-center items-center py-12">
              <Loader className="animate-spin text-accent-green" size={40} />
            </div>
          )}

          {!loadingFixtures && selectedFixtures.length > 0 && (
            <>
              <div className="space-y-3 mb-4">
                {selectedFixtures.map((fixture) => {
                  const fixtureDate = new Date(fixture.date)
                  
                  return (
                    <div
                      key={fixture.id}
                      onClick={() => !hasParticipants && toggleFixture(fixture)}
                      className={`p-4 border-2 rounded-lg ${hasParticipants ? '' : 'cursor-pointer'} transition-all border-accent-green bg-green-50`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex items-center gap-2 flex-1">
                              {fixture.teams.home.logo && (
                                <img 
                                  src={fixture.teams.home.logo} 
                                  alt={fixture.teams.home.name}
                                  className="w-6 h-6 object-contain"
                                />
                              )}
                              <span className="font-medium text-gray-900">
                                {fixture.teams.home.name}
                              </span>
                            </div>
                            <span className="text-gray-500 font-bold">VS</span>
                            <div className="flex items-center gap-2 flex-1 justify-end">
                              <span className="font-medium text-gray-900">
                                {fixture.teams.away.name}
                              </span>
                              {fixture.teams.away.logo && (
                                <img 
                                  src={fixture.teams.away.logo} 
                                  alt={fixture.teams.away.name}
                                  className="w-6 h-6 object-contain"
                                />
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {fixtureDate.toLocaleDateString('es-MX', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {fixture.venue && (
                              <span className="text-gray-500">• {fixture.venue}</span>
                            )}
                          </div>
                        </div>
                        <div className="ml-4 w-6 h-6 rounded border-2 flex items-center justify-center bg-accent-green border-accent-green">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>{selectedFixtures.length}</strong> partidos seleccionados
                </p>
              </div>
            </>
          )}

          {errors.matchIds && (
            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle size={14} /> {errors.matchIds}
            </p>
          )}
        </div>

        {/* Configuración Adicional */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Users size={20} />
            Límites y Restricciones
          </h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Límite Máximo de Participantes (opcional)
            </label>
            <input
              type="number"
              name="maxLimit"
              value={formData.maxLimit}
              onChange={handleChange}
              min="2"
              className={`input ${errors.maxLimit ? 'border-red-500' : ''}`}
              placeholder="Dejar vacío para sin límite"
            />
            {errors.maxLimit && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle size={14} /> {errors.maxLimit}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Si no se especifica, no habrá límite de participantes
            </p>
          </div>
        </div>

        {/* Botones */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/operator')}
            className="btn bg-gray-200 text-gray-700 hover:bg-gray-300 flex-1"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn bg-accent-green text-white hover:bg-green-700 flex-1 flex items-center justify-center gap-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={20} />
                Actualizando...
              </>
            ) : (
              'Guardar Cambios'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
